import {useEffect, useCallback, useRef} from 'react'
import {useSelectSettings} from '../../store'
import {clearIdentitySession} from '../auth/clear_identity_session'

export const useAutoLogoutTracker = (arg: {timeoutMinutes: number}) => {
  const {timeoutMinutes} = arg
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const settings = useSelectSettings()

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now()

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(
      () => {
        void clearIdentitySession().then(() => {
          window.location.reload()
        })
      },
      timeoutMinutes * 60 * 1000,
    )
  }, [timeoutMinutes])

  const handleActivity = useCallback(() => {
    resetTimer()
  }, [resetTimer])

  useEffect(() => {
    // Only enable auto logout when the setting is explicitly true.
    // If settings are undefined/null or the flag is false/null, disable the behavior.
    const isEnabled = settings?.webAutoTimeoutLogoutEnabled === true

    if (!isEnabled) {
      // Ensure any existing timers are cleared when disabling
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      return
    }
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    let throttleTimeout: NodeJS.Timeout
    const throttledHandler = () => {
      if (throttleTimeout) return

      throttleTimeout = setTimeout(() => {
        handleActivity()
        throttleTimeout = null as any
      }, 1000) // Throttle to once per second
    }

    events.forEach((event) => {
      document.addEventListener(event, throttledHandler, true)
    })

    resetTimer()

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledHandler, true)
      })
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout)
      }
    }
  }, [handleActivity, resetTimer, settings?.webAutoTimeoutLogoutEnabled])

  return {
    resetTimer,
  }
}
